/**
 * AI Trial Room · Phase 3 · Photo Upload Mode (Apparels)
 * Uses MediaPipe PoseLandmarker (33 body points) to detect torso and overlay apparel PNGs.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';
import { toast } from 'sonner';
import { Upload, Download, X, Loader2, Sparkles, RotateCcw } from 'lucide-react';
import api from '../../utils/api';
import AIAdviceButton from './AIAdviceButton';
import AIBestMatchButton from './AIBestMatchButton';

const POSE_MODEL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';
const WASM_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';

// MediaPipe Pose landmarks
const P = {
  leftShoulder: 11, rightShoulder: 12,
  leftHip: 23, rightHip: 24,
  nose: 0, leftElbow: 13, rightElbow: 14,
};

const drawApparel = (ctx, pose, w, h, img, subcategory) => {
  if (!img || !img.complete || img.naturalWidth === 0) return;
  const lm = (i) => ({ x: pose[i].x * w, y: pose[i].y * h });
  const lS = lm(P.leftShoulder), rS = lm(P.rightShoulder);
  const lH = lm(P.leftHip), rH = lm(P.rightHip);
  const shoulderMid = { x: (lS.x + rS.x) / 2, y: (lS.y + rS.y) / 2 };
  const hipMid = { x: (lH.x + rH.x) / 2, y: (lH.y + rH.y) / 2 };
  const shoulderWidth = Math.abs(rS.x - lS.x);
  const torsoHeight = Math.abs(hipMid.y - shoulderMid.y);
  const sub = (subcategory || '').toLowerCase();
  // Scale factors per subcategory
  let widthMul = 2.0, heightMul = 2.4, topOffsetMul = 0.25;
  if (/saree|lehenga|gown/.test(sub))      { widthMul = 2.4; heightMul = 3.8; topOffsetMul = 0.25; }
  else if (/jacket|coat|blazer/.test(sub)) { widthMul = 2.1; heightMul = 1.7; topOffsetMul = 0.15; }
  else if (/shirt|t.?shirt|top/.test(sub)) { widthMul = 2.0; heightMul = 1.6; topOffsetMul = 0.20; }
  else if (/kurta|kurti/.test(sub))        { widthMul = 2.1; heightMul = 2.4; topOffsetMul = 0.20; }
  const overlayWidth = shoulderWidth * widthMul;
  const overlayHeight = torsoHeight * heightMul;
  const x = shoulderMid.x - overlayWidth / 2;
  const y = shoulderMid.y - shoulderWidth * topOffsetMul;
  ctx.drawImage(img, x, y, overlayWidth, overlayHeight);
};

const TrialRoomPhotoUpload = ({ category, categoryTitle, onClose }) => {
  const canvasRef = useRef(null);
  const landmarkerRef = useRef(null);
  const photoRef = useRef(null);      // uploaded photo Image
  const productImgRef = useRef(null); // selected apparel Image
  const poseRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [aiPicks, setAiPicks] = useState([]);
  const [photoUrl, setPhotoUrl] = useState('');
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('🔄 Loading AI model...');
  const [processing, setProcessing] = useState(false);
  // Manual adjustments
  const [scale, setScale] = useState(1);
  const [offsetY, setOffsetY] = useState(0);

  // Load model + products
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pub, mine] = await Promise.all([
          api.get(`/trial-room/products?category=${category}`),
          api.get(`/trial-room/my-assigned-products?category=${category}`).catch(() => ({ data: { products: [] } })),
        ]);
        if (cancelled) return;
        setProducts([...(mine.data?.products || []), ...(pub.data?.products || [])]);
        setStatus('🔄 Loading Pose model...');
        const filesetResolver = await FilesetResolver.forVisionTasks(WASM_URL);
        const pl = await PoseLandmarker.createFromOptions(filesetResolver, {
          baseOptions: { modelAssetPath: POSE_MODEL, delegate: 'GPU' },
          runningMode: 'IMAGE', numPoses: 1,
        });
        if (cancelled) { pl.close(); return; }
        landmarkerRef.current = pl;
        setReady(true);
        setStatus('');
      } catch (e) {
        if (!cancelled) { setErr(`Failed: ${e.message || e}`); setStatus(''); toast.error(`❌ ${e.message || e}`); }
      }
    })();
    return () => {
      cancelled = true;
      if (landmarkerRef.current) { try { landmarkerRef.current.close(); } catch (_) {} }
    };
  }, [category]);

  // Load photo Image element when photoUrl changes
  useEffect(() => {
    if (!photoUrl) { photoRef.current = null; poseRef.current = null; return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      photoRef.current = img;
      // Detect pose once
      if (landmarkerRef.current) {
        try {
          const result = landmarkerRef.current.detect(img);
          poseRef.current = result?.landmarks?.[0] || null;
          if (!poseRef.current) toast.error('⚠️ Body pose not detected — try a clear full-body/torso photo');
          else toast.success('✅ Body detected! Pick apparel to try on.');
        } catch (e) { toast.error('Pose detection failed'); }
      }
      render();
    };
    img.onerror = () => toast.error('Photo load failed');
    img.src = photoUrl;
  }, [photoUrl]);

  // Load product image
  useEffect(() => {
    if (!selected) { productImgRef.current = null; render(); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { productImgRef.current = img; render(); };
    img.onerror = () => { productImgRef.current = null; toast.error('Product image load failed'); };
    img.src = selected.image_url;
  }, [selected]);

  // Render function — draws photo + apparel overlay
  const render = () => {
    const canvas = canvasRef.current, photo = photoRef.current;
    if (!canvas || !photo) return;
    // Fit canvas to photo aspect ratio (max 800x800)
    const MAX = 800;
    const ratio = photo.naturalWidth / photo.naturalHeight;
    let w = photo.naturalWidth, h = photo.naturalHeight;
    if (w > MAX) { w = MAX; h = MAX / ratio; }
    if (h > MAX) { h = MAX; w = MAX * ratio; }
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(photo, 0, 0, w, h);
    if (poseRef.current && productImgRef.current) {
      // Apply user scale/offsetY adjustment on top of computed
      const adjustedPose = poseRef.current.map((p) => ({ x: p.x, y: p.y + offsetY / h, z: p.z }));
      ctx.save();
      // scale relative to torso center
      const lS = adjustedPose[P.leftShoulder], rS = adjustedPose[P.rightShoulder];
      const cx = ((lS.x + rS.x) / 2) * w, cy = ((lS.y + rS.y) / 2) * h;
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);
      ctx.translate(-cx, -cy);
      drawApparel(ctx, adjustedPose, w, h, productImgRef.current, selected?.subcategory);
      ctx.restore();
    }
  };

  useEffect(() => { render(); }, [scale, offsetY]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('Photo <8 MB honi chahiye'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoUrl(ev.target.result);
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  const download = () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `TrialRoom-${category}-${Date.now()}.jpg`; a.click();
      URL.revokeObjectURL(url);
      toast.success('✅ Saved!');
    }, 'image/jpeg', 0.92);
  };

  const reset = () => { setPhotoUrl(''); setSelected(null); setScale(1); setOffsetY(0); };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="trial-room-photo">
      <div className="p-3 bg-slate-950 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black">🪞 Photo Try-On · Apparels</div>
          <h3 className="text-sm md:text-base font-black text-white">{categoryTitle || category}</h3>
        </div>
        <div className="flex items-center gap-2">
          {photoUrl && <button onClick={reset} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reset</button>}
          {photoUrl && ready && <AIAdviceButton canvasRef={canvasRef} category={category} product={selected} />}
          {photoUrl && ready && <button onClick={download} data-testid="photo-download-btn" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-black flex items-center gap-1"><Download className="w-3 h-3" /> Save</button>}
          <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center relative bg-slate-950 overflow-auto p-4">
        {!ready && !err && (
          <div className="flex flex-col items-center text-white">
            <Loader2 className="w-12 h-12 animate-spin text-fuchsia-400" />
            <div className="mt-3 text-sm">{status}</div>
          </div>
        )}
        {err && <div className="text-red-300 text-sm">{err}</div>}
        {ready && !photoUrl && (
          <label data-testid="photo-upload-label" className="cursor-pointer max-w-md rounded-2xl border-2 border-dashed border-fuchsia-400/50 bg-fuchsia-500/5 hover:bg-fuchsia-500/10 p-10 text-center transition">
            <input type="file" accept="image/*" onChange={handleFile} data-testid="photo-upload-input" className="hidden" />
            <Upload className="w-12 h-12 mx-auto text-fuchsia-300 mb-3" />
            <div className="text-lg font-black text-white">Upload Your Photo</div>
            <div className="text-xs text-slate-300 mt-1">Full-body or torso photo · JPG/PNG · Max 8 MB</div>
            <div className="text-[10px] text-fuchsia-300 mt-2">💡 Better lighting + straight pose = best fit</div>
          </label>
        )}
        {ready && photoUrl && (
          <canvas ref={canvasRef} className="max-w-full max-h-full shadow-2xl rounded-xl" />
        )}
      </div>

      {/* Bottom: products + adjustments */}
      {ready && photoUrl && (
        <div className="bg-slate-950 border-t border-white/10 p-2 space-y-1.5">
          {selected && (
            <div className="flex items-center justify-center gap-3 text-[11px] text-slate-200">
              <label className="flex items-center gap-1">Size: <input type="range" min="0.6" max="1.5" step="0.05" value={scale} onChange={(e) => setScale(Number(e.target.value))} data-testid="photo-scale" className="w-24" /> <span className="w-8 font-mono">{scale.toFixed(2)}x</span></label>
              <label className="flex items-center gap-1">Y: <input type="range" min="-100" max="100" step="5" value={offsetY} onChange={(e) => setOffsetY(Number(e.target.value))} data-testid="photo-offset" className="w-24" /> <span className="w-10 font-mono">{offsetY}px</span></label>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black">Choose Apparel ({products.length} available)</div>
            {ready && products.length > 0 && (
              <AIBestMatchButton canvasRef={canvasRef} category={category} onPicks={(ids) => setAiPicks(ids)} />
            )}
          </div>
          {products.length === 0 ? (
            <div className="text-xs text-slate-500 italic text-center py-2">Koi apparel available nahi. Admin panel se add karein.</div>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {products.map((p) => (
                <button key={p.id} onClick={() => setSelected(p)} data-testid={`photo-product-${p.id}`}
                  className={`flex-shrink-0 w-16 rounded-lg overflow-hidden border-2 transition relative ${selected?.id === p.id ? 'border-fuchsia-400 ring-2 ring-fuchsia-400/50' : aiPicks.includes(p.id) ? 'border-amber-400 ring-2 ring-amber-400/60 animate-pulse' : 'border-white/10 hover:border-white/30'}`}>
                  {aiPicks.includes(p.id) && <div className="absolute top-0 right-0 text-[7px] px-1 py-0 rounded-bl bg-amber-500 text-black font-black z-10">AI</div>}
                  <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-16 object-cover bg-slate-800" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                  <div className="text-[8px] text-white truncate p-0.5 bg-black/50">{p.name}</div>
                </button>
              ))}
              {selected && <button onClick={() => setSelected(null)} className="flex-shrink-0 w-16 h-16 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-200 text-[10px] font-bold flex flex-col items-center justify-center border-2 border-red-400/40"><X className="w-3 h-3" /> Clear</button>}
            </div>
          )}
          <div className="text-[9px] text-slate-500 text-center"><Sparkles className="w-3 h-3 inline text-fuchsia-400" /> MediaPipe Pose · Torso detection · Adjust size + position with sliders</div>
        </div>
      )}
    </div>
  );
};

export default TrialRoomPhotoUpload;
